import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { postPayment } from '../../../store/payment';
import { postRequest } from '../../../store/request';
import usersData from '../../../data/users.json';
import balanceService from '../../../utils/balanceService';
import './TransactionForm.css';

const TransactionForm = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const sessionUser = useSelector(state => state.session.user);
  
  // Use centralized user data with updated balances
  const staticUsers = balanceService.getAllUsers() || [];
  const currentUser = staticUsers.find(user => user.id === (sessionUser?.id || 1)) || staticUsers[0];
  const [currentBalance, setCurrentBalance] = useState(currentUser?.balance || 0);
  
  // Form states
  const [selectedUser, setSelectedUser] = useState(null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [transactionType, setTransactionType] = useState('pay');
  const [privacy, setPrivacy] = useState('friends');
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [errors, setErrors] = useState([]);

  // Get recent transaction users and update balance
  useEffect(() => {
    // Update current balance
    const updatedBalance = balanceService.getUserBalance(currentUser.id);
    setCurrentBalance(updatedBalance);

    // Get recent transactions
    const transactions = balanceService.getAllTransactions() || [];
    const recentUserIds = [...new Set(
      transactions
        .filter(t => t.sender_id === currentUser.id || t.receiver_id === currentUser.id)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 6)
        .map(t => t.sender_id === currentUser.id ? t.receiver_id : t.sender_id)
    )];
    
    const recentUsersList = recentUserIds
      .map(id => staticUsers.find(user => user.id === id))
      .filter(Boolean);
    
    setRecentUsers(recentUsersList);
  }, [currentUser.id]);

  // Filter users based on search term
  useEffect(() => {
    if (searchTerm.trim()) {
      const filtered = staticUsers.filter(user => 
        user.id !== currentUser.id && (
          user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user?.username?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      ).slice(0, 8);
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers([]);
    }
  }, [searchTerm, currentUser.id]);

  const formatCurrency = (balance) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(balance);
  };

  const validateAmount = (value) => {
    const num = parseFloat(value);
    return !isNaN(num) && num > 0 && num <= 9999.99;
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setShowUserSearch(false);
    setSearchTerm('');
  };

  // Handle manual username input (when user types @username that doesn't exist in search)
  const handleManualUserInput = (username) => {
    // Create a temporary user object for manual username
    const manualUser = {
      id: `manual_${username}`, // Use manual prefix for identification
      name: username,
      username: username.replace('@', ''),
      profile_picture: '/default-avatar.png', // Default avatar
      isManual: true // Flag to identify manual entries
    };
    
    setSelectedUser(manualUser);
    setShowUserSearch(false);
    setSearchTerm('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);

    const newErrors = [];
    
    if (!selectedUser) {
      newErrors.push('Please select a recipient');
    }
    
    if (!validateAmount(amount)) {
      newErrors.push('Please enter a valid amount between $0.01 and $9,999.99');
    }
    
    if (!note.trim()) {
      newErrors.push('Please add a note for this transaction');
    }
    
    if (parseFloat(amount) > currentBalance && transactionType === 'pay') {
      newErrors.push('Insufficient balance for this payment');
    }

    if (newErrors.length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const transactionAmount = parseFloat(amount);

      if (transactionType === 'pay') {
        // Handle manual users (create transaction record but don't update balances for non-existent users)
        if (selectedUser.isManual) {
          // For manual users, we'll create a transaction record but note it's to an external user
          const transactionData = {
            id: Date.now(),
            sender_id: currentUser.id,
            receiver_id: selectedUser.id,
            amount: transactionAmount,
            note: note.trim(),
            privacy: privacy,
            status: 'completed',
            created_at: new Date().toISOString(),
            receiver_name: selectedUser.username,
            isManual: true
          };

          // Add to transaction history
          const transactions = JSON.parse(localStorage.getItem('venmo_transactions') || '[]');
          transactions.unshift(transactionData);
          localStorage.setItem('venmo_transactions', JSON.stringify(transactions));

          // Update sender balance
          const updatedBalance = currentUser.balance - transactionAmount;
          balanceService.updateUserBalance(currentUser.id, updatedBalance);
          setCurrentBalance(updatedBalance);
        } else {
          // Process payment with balance reduction for existing users
          const result = balanceService.processPayment(
            currentUser.id,
            selectedUser.id,
            transactionAmount,
            note.trim(),
            privacy
          );

          if (!result.success) {
            setErrors([result.error]);
            return;
          }

          // Update local balance state
          setCurrentBalance(result.updatedSender.balance);
        }

        // Also dispatch to Redux store for compatibility
        await dispatch(postPayment({
          amount: transactionAmount,
          receiver_id: selectedUser.id,
          sender_id: currentUser.id,
          title: note.trim(),
          privacy: privacy
        }));

        // Navigate to success page with transaction data
        history.push('/transaction-success', {
          amount: transactionAmount,
          recipient: selectedUser.name,
          type: 'payment',
          note: note.trim()
        });

      } else {
        // Process request (no balance change)
        const result = balanceService.processRequest(
          currentUser.id,
          selectedUser.id,
          transactionAmount,
          note.trim(),
          privacy
        );

        if (!result.success) {
          setErrors([result.error]);
          return;
        }

        // Dispatch to Redux store
        await dispatch(postRequest({
          amount: transactionAmount,
          receiver_id: selectedUser.id,
          sender_id: currentUser.id,
          title: note.trim(),
          privacy: privacy
        }));

        // Navigate to success page with request data
        history.push('/transaction-success', {
          amount: transactionAmount,
          recipient: selectedUser.name,
          type: 'request',
          note: note.trim()
        });
      }

    } catch (error) {
      setErrors(['Transaction failed. Please try again.']);
    }
  };

  return (
    <div className="transaction-form">
      <div className="form-header">
        <h2>{transactionType === 'pay' ? 'Send Money' : 'Request Money'}</h2>
        <p>Send or request money from friends and family</p>
      </div>

      {/* Balance Info */}
      <div className="balance-info">
        <div className="balance-label">Your Venmo Balance</div>
        <div className="balance-amount">{formatCurrency(currentBalance)}</div>
      </div>

      {/* Transaction Type Toggle */}
      <div className="transaction-type-toggle">
        <button
          type="button"
          className={`type-option ${transactionType === 'pay' ? 'active' : ''}`}
          onClick={() => setTransactionType('pay')}
        >
          <i className="fas fa-arrow-up"></i>
          Pay
        </button>
        <button
          type="button"
          className={`type-option ${transactionType === 'request' ? 'active' : ''}`}
          onClick={() => setTransactionType('request')}
        >
          <i className="fas fa-arrow-down"></i>
          Request
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* User Selection */}
        <div className="user-selection">
          <label className="user-selection-label">
            {transactionType === 'pay' ? 'Send to' : 'Request from'}
          </label>
          
          {selectedUser ? (
            <div className="selected-user">
              <img
                src={selectedUser.profile_picture}
                alt={selectedUser.name}
              />
              <div className="selected-user-info">
                <div className="selected-user-name">{selectedUser.name}</div>
                <div className="selected-user-username">@{selectedUser.username}</div>
              </div>
              <button
                type="button"
                className="change-user-btn"
                onClick={() => {
                  setSelectedUser(null);
                  setShowUserSearch(true);
                }}
              >
                Change
              </button>
            </div>
          ) : (
            <div className="user-search">
              <div className="search-input-container">
                <i className="fas fa-search search-icon"></i>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search by name or @username"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => setShowUserSearch(true)}
                />
              </div>
              
              {searchTerm && (
                <div className="search-results">
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map(user => (
                      <div
                        key={user.id}
                        className="search-result-item"
                        onClick={() => handleUserSelect(user)}
                      >
                        <img src={user.profile_picture} alt={user.name} />
                        <div className="user-info">
                          <div className="user-name">{user.name}</div>
                          <div className="user-username">@{user.username}</div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-results">
                      <div className="no-results-text">
                        No users found for "{searchTerm}"
                      </div>
                      {searchTerm.startsWith('@') || searchTerm.match(/^[a-zA-Z0-9_]+$/) ? (
                        <div 
                          className="manual-user-option"
                          onClick={() => handleManualUserInput(searchTerm)}
                        >
                          <i className="fas fa-plus-circle"></i>
                          Send to @{searchTerm.replace('@', '')} anyway
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              )}
              
              {recentUsers.length > 0 && !searchTerm && (
                <div className="recent-users">
                  <div className="recent-users-label">Recent</div>
                  <div className="recent-users-grid">
                    {recentUsers.map(user => (
                      <div
                        key={user.id}
                        className="recent-user-item"
                        onClick={() => handleUserSelect(user)}
                      >
                        <img src={user.profile_picture} alt={user.name} />
                        <div className="recent-user-name">{user.name.split(' ')[0]}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Amount Input */}
        <div className="form-group">
          <label className="form-label">Amount</label>
          <input
            type="number"
            step="0.01"
            min="0.01"
            max="9999.99"
            className="amount-input"
            placeholder="$0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>

        {/* Note Input */}
        <div className="form-group">
          <label className="form-label">What's this for?</label>
          <textarea
            className="note-input"
            placeholder="Add a note..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={200}
          />
        </div>

        {/* Privacy Setting */}
        <div className="form-group">
          <label className="form-label">Privacy</label>
          <select
            className="privacy-select"
            value={privacy}
            onChange={(e) => setPrivacy(e.target.value)}
          >
            <option value="public">Public</option>
            <option value="friends">Friends</option>
            <option value="private">Private</option>
          </select>
        </div>

        {/* Error Messages */}
        {errors.length > 0 && (
          <div className="error-messages">
            {errors.map((error, index) => (
              <div key={index} className="error-message">
                <i className="fas fa-exclamation-circle"></i>
                {error}
              </div>
            ))}
          </div>
        )}

        {/* Action Buttons */}
        <div className="form-actions">
          <button
            type="button"
            className="cancel-btn"
            onClick={() => history.push('/')}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="submit-btn"
            disabled={!selectedUser || !amount || !note.trim()}
          >
            {transactionType === 'pay' ? 'Send Payment' : 'Send Request'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TransactionForm;